import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChartIcon, FlagIcon, LogOutIcon, MessageCircleIcon, ShieldIcon, UserIcon } from '../components/ui/icons';
import { Megaphone, Gift, FileText, Pin, ClipboardList, Bell, MousePointerClick, Bot, Trophy, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { InquiryManagement } from '../components/admin/InquiryManagement';

import { ReportManagement } from '../components/admin/ReportManagement';
import { UserManagement } from '../components/admin/UserManagement';
import { AdManagement } from '../components/admin/AdManagement';
import { ServiceAnalyticsDashboard } from '../components/admin/ServiceAnalyticsDashboard';
import { RewardManagement } from '../components/admin/RewardManagement';
import { GuideManagement } from '../components/admin/GuideManagement';
import { SurveyPinManagement } from '../components/admin/SurveyPinManagement';
import { SurveyLimitPanel } from '../components/admin/SurveyLimitPanel';
import { HistoryManagement } from '../components/admin/HistoryManagement';
import { NoticeManagement } from '../components/admin/NoticeManagement';
import { ClickManagement } from '../components/admin/ClickManagement';
import { BotManagement } from '../components/admin/BotManagement';
import { BotPendingSurveyPanel } from '../components/admin/BotPendingSurveyPanel';
import { LeaderboardManagement } from '../components/admin/LeaderboardManagement';

/**
 * 관리자 대시보드 페이지
 *
 * @author hjkim
 * @constructor
 */
const AdminDashboard = () => {
  /* Navigator */
  const navigate = useNavigate();

  /* States */
  const [activeTab, setActiveTab] = useState<string>('rewards');

  /**
   * Handlers
   */
  /* 페이지 이동 handler */
  const navigateTo = (path: string) => {
    if (path === 'dashboard') {
      console.log('dashboard');
      navigate('/');
    }
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-red-50/30">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* 헤더 */}
        <div className="mb-8 space-y-6">
          {/* 관리자 프로필 헤더 */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div className="relative">
                  <div className="w-16 h-16 bg-gradient-to-br from-red-300 to-red-400 rounded-2xl flex items-center justify-center shadow-lg">
                    <ShieldIcon className="w-6 h-6 text-white" />
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-red-400 rounded-full border-2 border-white flex items-center justify-center">
                    <span className="text-xs text-white">⚡</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">관리자 대시보드</h1>
                  <p className="text-gray-600 text-sm sm:text-base">플랫폼의 모든 활동을 모니터링하고 관리하세요.</p>
                </div>
              </div>
              {/* 버튼 그룹 */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => navigateTo('dashboard')}
                  className="flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300 hover:text-red-700 transition-all duration-200"
                >
                  <LogOutIcon className="w-4 h-4" />
                  <span className="hidden sm:inline">메인으로</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto p-6">
        {/* 탭 메뉴 */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="flex flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="rewards" className="flex items-center gap-1.5 text-xs sm:text-sm px-2.5 sm:px-3">
              <Gift className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              리워드
            </TabsTrigger>
            <TabsTrigger value="inquiry" className="flex items-center gap-1.5 text-xs sm:text-sm px-2.5 sm:px-3">
              <MessageCircleIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              문의 관리
            </TabsTrigger>
            <TabsTrigger value="reports" className="flex items-center gap-1.5 text-xs sm:text-sm px-2.5 sm:px-3">
              <FlagIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              신고 관리
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-1.5 text-xs sm:text-sm px-2.5 sm:px-3">
              <UserIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              사용자 관리
            </TabsTrigger>
            <TabsTrigger value="ads" className="flex items-center gap-1.5 text-xs sm:text-sm px-2.5 sm:px-3">
              <Megaphone className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              광고 관리
            </TabsTrigger>
            <TabsTrigger value="guides" className="flex items-center gap-1.5 text-xs sm:text-sm px-2.5 sm:px-3">
              <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              안내문구
            </TabsTrigger>
            <TabsTrigger value="notices" className="flex items-center gap-1.5 text-xs sm:text-sm px-2.5 sm:px-3">
              <Bell className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              공지사항
            </TabsTrigger>
            <TabsTrigger value="surveys" className="flex items-center gap-1.5 text-xs sm:text-sm px-2.5 sm:px-3">
              <Pin className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              설문 관리
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-1.5 text-xs sm:text-sm px-2.5 sm:px-3">
              <ClipboardList className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              내역관리
            </TabsTrigger>
            <TabsTrigger value="clicks" className="flex items-center gap-1.5 text-xs sm:text-sm px-2.5 sm:px-3">
              <MousePointerClick className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              클릭관리
            </TabsTrigger>
            <TabsTrigger value="bot" className="flex items-center gap-1.5 text-xs sm:text-sm px-2.5 sm:px-3">
              <Bot className="w-3.5 h-3.5 sm:w-4 sm:h-4" />봇 관리
            </TabsTrigger>
            <TabsTrigger value="bot-pending" className="flex items-center gap-1.5 text-xs sm:text-sm px-2.5 sm:px-3">
              <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              자동발행 대기
            </TabsTrigger>
            <TabsTrigger value="leaderboard" className="flex items-center gap-1.5 text-xs sm:text-sm px-2.5 sm:px-3">
              <Trophy className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              리더보드
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-1.5 text-xs sm:text-sm px-2.5 sm:px-3">
              <BarChartIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              통계
            </TabsTrigger>
          </TabsList>
          {/* 문의 관리 탭 */}
          <TabsContent value="inquiry" className="space-y-6">
            <InquiryManagement />
          </TabsContent>
          {/* 신고관리 탭 */}
          <TabsContent value="reports" className="space-y-6">
            <ReportManagement />
          </TabsContent>
          {/* 사용자 관리 탭 */}
          <TabsContent value="users" className="space-y-6">
            <UserManagement />
          </TabsContent>
          {/* 광고 관리 탭 */}
          <TabsContent value="ads" className="space-y-6">
            <AdManagement />
          </TabsContent>
          {/* 리워드 관리 탭 */}
          <TabsContent value="rewards" className="space-y-6">
            <RewardManagement />
          </TabsContent>
          {/* 안내문구 관리 탭 */}
          <TabsContent value="guides" className="space-y-6">
            <GuideManagement />
          </TabsContent>
          {/* 공지사항 관리 탭 */}
          <TabsContent value="notices" className="space-y-6">
            <NoticeManagement />
          </TabsContent>
          {/* 설문 관리 탭 */}
          <TabsContent value="surveys" className="space-y-6">
            <SurveyLimitPanel />
            <SurveyPinManagement />
          </TabsContent>
          {/* 내역관리 탭 */}
          <TabsContent value="history" className="space-y-6">
            <HistoryManagement />
          </TabsContent>
          {/* 클릭관리 탭 */}
          <TabsContent value="clicks" className="space-y-6">
            <ClickManagement />
          </TabsContent>
          {/* 봇 관리 탭 */}
          <TabsContent value="bot" className="space-y-6">
            <BotManagement onGoPending={() => setActiveTab('bot-pending')} />
          </TabsContent>
          {/* 자동발행 대기 탭 (S0-29 트랙 B) */}
          <TabsContent value="bot-pending" className="space-y-6">
            <BotPendingSurveyPanel />
          </TabsContent>
          {/* 리더보드 탭 */}
          <TabsContent value="leaderboard" className="space-y-6">
            <LeaderboardManagement />
          </TabsContent>
          {/* 통계 탭 */}
          <TabsContent value="analytics" className="space-y-6">
            <ServiceAnalyticsDashboard />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
